import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBenefitProgramsV1Balance from './benefit-programs-v1-balance-balances.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface Balance {
  /**
   * Balance ID. This is the same as the associated pool's ID.
   * @format GUID
   * @immutable
   * @readonly
   */
  _id?: string;
  /**
   * Revision number, which increments by 1 each time the balance is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the balance.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the balance was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the balance was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Beneficiary of the associated pool.
   * @readonly
   * @immutable
   */
  beneficiary?: CommonIdentificationData;
  /**
   * Available credits.
   * @readonly
   */
  amount?: BalanceAmount;
  /**
   * Associated pool information.
   * @readonly
   */
  poolInfo?: PoolInfo;
  /**
   * Custom field data for the balance object.
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * ID of the transaction associated with the most recent change to the balance.
   * @format GUID
   * @readonly
   */
  lastTransactionId?: string | null;
}

export interface CommonIdentificationData
  extends CommonIdentificationDataIdOneOf {
  /**
   * ID of a site visitor that hasn't logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site member.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user.
   * @format GUID
   */
  wixUserId?: string;
  /**
   * Identity type.
   * @internal
   * @readonly
   */
  identityType?: IdentityTypeWithLiterals;
}

/** @oneof */
export interface CommonIdentificationDataIdOneOf {
  /**
   * ID of a site visitor that hasn't logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site member.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user.
   * @format GUID
   */
  wixUserId?: string;
}

export enum IdentityType {
  /** Unknown type. This value is not used. */
  UNKNOWN = 'UNKNOWN',
  /** A site visitor who has not logged in. */
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  /** A logged-in site member. */
  MEMBER = 'MEMBER',
  /** A Wix account holder, such as a site owner or contributor. */
  WIX_USER = 'WIX_USER',
}

/** @enumType */
export type IdentityTypeWithLiterals =
  | IdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER';

export interface BalanceAmount {
  /**
   * number of available credits.
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  available?: string;
  /**
   * number of reserved credits.
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  reserved?: string;
}

export interface PoolInfo {
  /**
   * Pool ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * ID of the pool definition the pool was created from.
   * @format GUID
   * @readonly
   */
  poolDefinitionId?: string | null;
  /**
   * ID of the program definition the pool was created from.
   * @format GUID
   * @readonly
   */
  programDefinitionId?: string | null;
  /**
   * ID of the program that contains the pool.
   * @format GUID
   * @readonly
   */
  programId?: string | null;
  /**
   * Available credits.
   * @decimalValue options { gte:0, maxScale:4 }
   * @readonly
   */
  creditAmount?: string | null;
  /**
   * Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created.
   * @minLength 1
   * @maxLength 20
   * @readonly
   */
  namespace?: string | null;
  /**
   * External ID of the program definition the pool was created from.
   * @format GUID
   * @readonly
   */
  externalProgramDefinitionId?: string | null;
  /**
   * External ID of the program that contains the pool.
   * @format GUID
   * @readonly
   */
  externalProgramId?: string | null;
  /**
   * Credit rollover configuration information for the pool.
   * @readonly
   */
  creditRolloverConfiguration?: RolloverConfigurationInfo;
  /**
   * Pool status
   * @readonly
   */
  status?: PoolStatusWithLiterals;
}

export interface RolloverConfigurationInfo {
  /**
   * Whether unused credits roll over to a new cycle when a program renews.
   * @readonly
   */
  enabled?: boolean | null;
  /**
   * Maximum number of credits that can roll over to the next cycle when a program renews.
   * @decimalValue options { gte:0, maxScale:4 }
   * @readonly
   */
  balanceCap?: string | null;
}

export enum PoolStatus {
  /** Pool is active. */
  ACTIVE = 'ACTIVE',
  /** Pool is paused. It can be resumed. */
  PAUSED = 'PAUSED',
  /** Pool is ended. It can't be resumed. */
  ENDED = 'ENDED',
  /** Pool provisioning is in progress. This status exists for a short time during processing. */
  PROVISIONING = 'PROVISIONING',
  /** Pool renewing is in progress. This status exists for a short time during processing. */
  RENEWING = 'RENEWING',
  /** Pool is pending */
  PENDING = 'PENDING',
}

/** @enumType */
export type PoolStatusWithLiterals =
  | PoolStatus
  | 'ACTIVE'
  | 'PAUSED'
  | 'ENDED'
  | 'PROVISIONING'
  | 'RENEWING'
  | 'PENDING';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface GetBalanceRequest {
  /**
   * ID of the pool associated with the balance to retrieve. This is also the ID of the balance.
   * @format GUID
   */
  poolId: string;
}

export interface GetBalanceResponse {
  /** Retrieved balance. */
  balance?: Balance;
}

export interface ListBalancesRequest {
  /**
   * List of IDs of the pools associated with the balances to retrieve. These are also the IDs of the balances.
   * @format GUID
   * @maxSize 100
   */
  poolIds?: string[];
  /** Cursor paging. */
  cursorPaging?: CursorPaging;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface ListBalancesResponse {
  /**
   * Retrieved balances.
   * @maxSize 100
   */
  balances?: Balance[];
  /** Metadata for paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface QueryBalancesRequest {
  /** Filter, sort, and paging to apply to the query. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
   * for more information.
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /**
   * Sort order. Use `ASC` for ascending order or `DESC` for descending order.
   *
   * Default: `ASC`
   */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
}

export enum SortOrder {
  /** Ascending sort order. */
  ASC = 'ASC',
  /** Descending sort order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface QueryBalancesResponse {
  /** List of retrieved balances. */
  balances?: Balance[];
  /** Metadata for the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface BalanceChangeBalanceRequest
  extends BalanceChangeBalanceRequestOperationOneOf {
  /** Adjust the balance by a specific number of credits. */
  adjustOptions?: AdjustOptions;
  /** Set the balance to a specific number of credits. */
  setOptions?: SetOptions;
  /**
   * Sets the initial balance to initialize the balance. If this isn't the first operation on the balance, the call will fail.
   * @internal
   */
  setInitialOptions?: SetInitialOptions;
  /**
   * ID of the pool associated with the balance to change. This is also the ID of the balance.
   * @format GUID
   */
  poolId: string;
  /**
   * Unique identifier, generated by the client.
   * Used to recognize repeated attempts to make the same request.
   * @maxLength 200
   */
  idempotencyKey: string;
  /** Identity changing the balance. */
  instructingParty?: CommonIdentificationData;
  /** Balance change type. */
  type?: ChangeBalanceRequestTypeWithLiterals;
  /** Details to send to the transaction created from this balance change. */
  transactionDetails?: TransactionDetails;
}

/** @oneof */
export interface BalanceChangeBalanceRequestOperationOneOf {
  /** Adjust the balance by a specific number of credits. */
  adjustOptions?: AdjustOptions;
  /** Set the balance to a specific number of credits. */
  setOptions?: SetOptions;
  /**
   * Sets the initial balance to initialize the balance. If this isn't the first operation on the balance, the call will fail.
   * @internal
   */
  setInitialOptions?: SetInitialOptions;
}

export enum ChangeBalanceRequestType {
  /** Unknown balance change type. */
  UNKNOWN_OPERATION = 'UNKNOWN_OPERATION',
  /** Use with `adjustOptions`. */
  ADJUST = 'ADJUST',
  /** Use with `setOptions`. */
  SET = 'SET',
  /** @internal */
  SET_INITIAL = 'SET_INITIAL',
}

/** @enumType */
export type ChangeBalanceRequestTypeWithLiterals =
  | ChangeBalanceRequestType
  | 'UNKNOWN_OPERATION'
  | 'ADJUST'
  | 'SET';

export interface AdjustOptions {
  /**
   * Amount to change the balance's available credits by.
   * - To increase the balance, specify a positive number.
   * - To decrease the balance, specify a negative number.
   * @decimalValue options { maxScale:4 }
   */
  value?: string;
  /** Beneficiary of the associated pool. */
  beneficiary?: CommonIdentificationData;
  /**
   * Cap the balance to the provided amount. If the balance is already lower than the floor, the balance will not be changed. Floor must be lower than the cap.
   * @internal
   * @decimalValue options { maxScale:4 }
   */
  lowerLimit?: string | null;
  /**
   * Cap the balance to the provided amount. If the balance is already lower than the floor, the balance will not be changed. Floor must be lower than the cap.
   * @internal
   * @decimalValue options { maxScale:4 }
   */
  upperLimit?: string | null;
  /**
   * Rule to apply when the balance is outside the provided limits.
   * @internal
   */
  limitRule?: LimitRuleWithLiterals;
  /**
   * Source balance type
   * @internal
   */
  sourceBalanceType?: BalanceTypeWithLiterals;
  /**
   * Target balance type
   * @internal
   */
  targetBalanceType?: BalanceTypeWithLiterals;
}

export enum LimitRule {
  /** If the limit is exceeded, the operation will fail */
  FAIL = 'FAIL',
  /** If the limit is exceeded, the operation will succeed but the balance never goes beyond the provided limits. For balance that was already outside of provided limits, it will not be changed. */
  SOFT_ENFORCE = 'SOFT_ENFORCE',
  /** If the limit is exceeded, the operation will succeed but the balance will be adjusted so that it satisfies the provided limits. If the resulting balance is below the lower_limit, it will be set to the lower_limit. If the resulting balance is above the upper_limit, it will be set to the upper_limit. */
  HARD_ENFORCE = 'HARD_ENFORCE',
}

/** @enumType */
export type LimitRuleWithLiterals =
  | LimitRule
  | 'FAIL'
  | 'SOFT_ENFORCE'
  | 'HARD_ENFORCE';

export enum BalanceType {
  /** In a pool's balance. */
  AVAILABLE = 'AVAILABLE',
  /**
   * Reserved balance.
   * @internal
   */
  RESERVED = 'RESERVED',
  /** Outside a pool's balance. */
  EXTERNAL = 'EXTERNAL',
}

/** @enumType */
export type BalanceTypeWithLiterals = BalanceType | 'AVAILABLE' | 'EXTERNAL';

export interface SetOptions {
  /**
   * Amount to set the balance's available credits to.
   *
   * If this is the same as the current value, no transaction is created.
   * @decimalValue options { maxScale:4 }
   */
  value?: string;
  /** Beneficiary of the associated pool. */
  beneficiary?: CommonIdentificationData;
  /**
   * Target balance type
   * @internal
   */
  targetBalanceType?: BalanceTypeWithLiterals;
}

export interface SetInitialOptions {
  /**
   * Set the available balance to the provided amount.
   * @decimalValue options { maxScale:4 }
   */
  value?: string;
  /** Benefit pool owner associated with this balance. */
  beneficiary?: CommonIdentificationData;
}

export interface TransactionDetails {
  /**
   * Item associated with the transaction.
   * @readonly
   */
  item?: Item;
  /** Amount of items associated with the transaction. */
  itemCount?: number | null;
  /**
   * Date and time the transaction was created.
   * @readonly
   * @immutable
   */
  effectiveDate?: Date | null;
  /**
   * Reason for the transaction. For example, `Redemption`.
   * @readonly
   * @immutable
   * @maxLength 256
   */
  reason?: string | null;
  /**
   * Transaction origin. Indicates the source of the transaction.
   * @internal
   * @immutable
   * @maxLength 50
   */
  origin?: string | null;
  /**
   * Benefit key associated with the transaction.
   * @maxLength 64
   * @immutable
   */
  benefitKey?: string | null;
}

export interface Item {
  /**
   * Item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Item external ID.
   * @format GUID
   * @readonly
   */
  externalId?: string | null;
  /**
   * Item category.
   * @maxLength 20
   * @readonly
   */
  category?: string | null;
  /**
   * Item set ID.
   * @format GUID
   * @readonly
   */
  itemSetId?: string | null;
  /**
   * Item name.
   * @maxLength 400
   * @readonly
   */
  displayName?: string | null;
  /**
   * Provider app id
   * @format GUID
   * @readonly
   */
  providerAppId?: string | null;
}

export interface ChangeBalanceResponse {
  /** Updated balance. */
  balance?: Balance;
  /**
   * ID of the transaction associated with this change in balance.
   * @format GUID
   */
  transactionId?: string | null;
}

export interface BalanceExceededLimits {
  /** Current balance */
  balance?: BalanceAmount;
  /**
   * The requested amount
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  requested?: string;
  /**
   * Lower limit
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  lowerLimit?: string | null;
  /**
   * Upper limit
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  upperLimit?: string | null;
  /** Source balance type */
  sourceBalanceType?: BalanceTypeWithLiterals;
  /** Target balance type */
  targetBalanceType?: BalanceTypeWithLiterals;
}

export interface BulkChangeBalancesRequest {
  /**
   * List of balances to change.
   * @minSize 1
   * @maxSize 100
   */
  requests?: ChangeBalanceRequest[];
  /**
   * Unique identifier, generated by the client.
   * Used to recognize repeated attempts to make the same request.
   * @maxLength 200
   */
  idempotencyKey?: string;
  /** Whether to return the full balance entities in the response. */
  returnEntity?: boolean;
}

export enum Type {
  /** Unknown balance change type. */
  UNKNOWN_OPERATION = 'UNKNOWN_OPERATION',
  /** @internal */
  SET_INITIAL = 'SET_INITIAL',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNKNOWN_OPERATION';

export interface ChangeBalanceRequest
  extends ChangeBalanceRequestOperationOneOf {
  /**
   * Sets the initial balance to initialize the balance. If this isn't the first operation on the balance, the call will fail.
   * @internal
   */
  setInitialOptions?: SetInitialOptions;
  /**
   * ID of the pool associated with the balance to change. This is also the ID of the balance.
   * @format GUID
   */
  poolId?: string;
  /** Identity changing the balance. */
  instructingParty?: CommonIdentificationData;
  /** Balance change type. */
  type?: TypeWithLiterals;
  /** Details to send to the transaction created from this balance change. */
  transactionDetails?: TransactionDetails;
}

/** @oneof */
export interface ChangeBalanceRequestOperationOneOf {
  /**
   * Sets the initial balance to initialize the balance. If this isn't the first operation on the balance, the call will fail.
   * @internal
   */
  setInitialOptions?: SetInitialOptions;
}

export interface BulkChangeBalancesResponse {
  /**
   * Updated item and associated metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkChangeBalanceResult[];
  /** Metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkChangeBalanceResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
  /** Updated item. */
  balance?: Balance;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface RevertBalanceChangeRequest {
  /**
   * ID of the transaction associated with the balance change to revert.
   * @format GUID
   */
  transactionId: string;
  /**
   * Ignored value, kept for backward compatibility. Idempotency is now handled by the system.
   * @maxLength 200
   * @deprecated Ignored value, kept for backward compatibility. Idempotency is now handled by the system.
   * @targetRemovalDate 2026-01-31
   */
  idempotencyKey: string;
  /** Identity reverting the balance change. */
  instructingParty?: CommonIdentificationData;
}

export interface RevertBalanceChangeResponse {
  /**
   * ID of the transaction associated with the balance reversion.
   * @format GUID
   */
  transactionId?: string | null;
}

export interface ChangeAlreadyReverted {
  /**
   * The id of the transaction which was already reverted
   * @format GUID
   */
  originalTransactionId?: string;
  /**
   * The id of the transaction which reverted the original transaction
   * @format GUID
   */
  revertedTransactionId?: string;
}

export interface NotEnoughBalance {
  /** Current balance */
  balance?: BalanceAmount;
  /**
   * The requested amount
   * @decimalValue options { maxScale:4 }
   * @readonly
   */
  requested?: string;
}

export interface GetTransactionReversibilityRequest {
  /**
   * Id of the transaction to get the reversibility
   * @format GUID
   */
  transactionId?: string;
}

export interface GetTransactionReversibilityResponse {
  /** The result of transaction reversibility validation */
  transactionReversibility?: TransactionReversibilityWithLiterals;
}

/** Transaction reversibility results */
export enum TransactionReversibility {
  /** Transaction is allowed to be reverted */
  TRANSACTION_IS_REVERSIBLE = 'TRANSACTION_IS_REVERSIBLE',
  /** Transaction isn't allowed to be reverted, because it was already reverted */
  TRANSACTION_ALREADY_REVERSED = 'TRANSACTION_ALREADY_REVERSED',
}

/** @enumType */
export type TransactionReversibilityWithLiterals =
  | TransactionReversibility
  | 'TRANSACTION_IS_REVERSIBLE'
  | 'TRANSACTION_ALREADY_REVERSED';

export interface HealBalanceRequest {
  /**
   * ID of the balance whose enrichments should be re-materialized. This is also the ID of the pool.
   * @format GUID
   */
  balanceId?: string;
}

export interface HealBalanceResponse {}

export interface HealBalanceTenantRequest {}

export interface HealBalanceTenantResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type ChangeBalanceApplicationErrors =
  | {
      code?: 'ALREADY_EXECUTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'BALANCE_ALREADY_INITIALIZED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'BALANCE_EXCEEDED_LIMITS';
      description?: string;
      data?: BalanceExceededLimits;
    };
/** @docsIgnore */
export type RevertBalanceChangeApplicationErrors =
  | {
      code?: 'CHANGE_ALREADY_REVERTED';
      description?: string;
      data?: ChangeAlreadyReverted;
    }
  | {
      code?: 'NOT_ENOUGH_BALANCE';
      description?: string;
      data?: NotEnoughBalance;
    }
  | {
      code?: 'TRANSACTION_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };

/**
 * Retrieves a balance.
 * @param poolId - ID of the pool associated with the balance to retrieve. This is also the ID of the balance.
 * @public
 * @requiredField poolId
 * @permissionId BENEFIT_PROGRAMS.BALANCE_READ
 * @applicableIdentity APP
 * @returns Retrieved balance.
 * @fqn wix.benefit_programs.v1.balance.BalanceService.GetBalance
 */
export async function getBalance(
  poolId: string
): Promise<
  NonNullablePaths<
    Balance,
    | `_id`
    | `beneficiary.anonymousVisitorId`
    | `beneficiary.memberId`
    | `beneficiary.wixUserId`
    | `amount.available`
    | `amount.reserved`
    | `poolInfo._id`
    | `poolInfo.status`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ poolId: poolId });

  const reqOpts = ambassadorWixBenefitProgramsV1Balance.getBalance(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.balance!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { poolId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['poolId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of balances.
 * @public
 * @permissionId BENEFIT_PROGRAMS.BALANCE_READ
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.balance.BalanceService.ListBalances
 */
export async function listBalances(
  options?: ListBalancesOptions
): Promise<
  NonNullablePaths<
    ListBalancesResponse,
    | `balances`
    | `balances.${number}._id`
    | `balances.${number}.beneficiary.anonymousVisitorId`
    | `balances.${number}.beneficiary.memberId`
    | `balances.${number}.beneficiary.wixUserId`
    | `balances.${number}.amount.available`
    | `balances.${number}.amount.reserved`
    | `balances.${number}.poolInfo._id`
    | `balances.${number}.poolInfo.status`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolIds: options?.poolIds,
    cursorPaging: options?.cursorPaging,
  });

  const reqOpts = ambassadorWixBenefitProgramsV1Balance.listBalances(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          poolIds: '$[0].poolIds',
          cursorPaging: '$[0].cursorPaging',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListBalancesOptions {
  /**
   * List of IDs of the pools associated with the balances to retrieve. These are also the IDs of the balances.
   * @format GUID
   * @maxSize 100
   */
  poolIds?: string[];
  /** Cursor paging. */
  cursorPaging?: CursorPaging;
}

/**
 * Creates a query to retrieve a list of balances.
 *
 * The Query Balances method builds a query to retrieve a list of balances and returns a `BalancesQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `BalancesQueryBuilder` methods onto the query. `BalancesQueryBuilder` methods enable you to filter, sort, and control the results that Query Balances returns.
 *
 * Query Balances has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `BalancesQueryResult`.
 * @public
 * @permissionId BENEFIT_PROGRAMS.BALANCE_READ
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.balance.BalanceService.QueryBalances
 */
export function queryBalances(): BalancesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Balance,
    'CURSOR',
    QueryBalancesRequest,
    QueryBalancesResponse
  >({
    func: async (payload: QueryBalancesRequest) => {
      const reqOpts =
        ambassadorWixBenefitProgramsV1Balance.queryBalances(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryBalancesRequest['query']) => {
      const args = [query, {}] as [QueryBalancesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryBalancesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.balances,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface BalancesQueryResult extends QueryCursorResult {
  items: Balance[];
  query: BalancesQueryBuilder;
  next: () => Promise<BalancesQueryResult>;
  prev: () => Promise<BalancesQueryResult>;
}

export interface BalancesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'beneficiary.memberId'
      | 'beneficiary.wixUserId',
    value: any
  ) => BalancesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'beneficiary.memberId'
      | 'beneficiary.wixUserId',
    value: any
  ) => BalancesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'beneficiary.memberId'
      | 'beneficiary.wixUserId',
    value: any
  ) => BalancesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'beneficiary.memberId'
      | 'beneficiary.wixUserId',
    value: any
  ) => BalancesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'beneficiary.memberId'
      | 'beneficiary.wixUserId',
    value: any
  ) => BalancesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'beneficiary.memberId'
      | 'beneficiary.wixUserId',
    value: any
  ) => BalancesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'beneficiary.memberId' | 'beneficiary.wixUserId',
    value: string
  ) => BalancesQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'beneficiary.memberId'
      | 'beneficiary.wixUserId',
    value: any
  ) => BalancesQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | 'beneficiary.memberId'
      | 'beneficiary.wixUserId',
    value: boolean
  ) => BalancesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      '_id' | '_createdDate' | 'beneficiary.memberId' | 'beneficiary.wixUserId'
    >
  ) => BalancesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      '_id' | '_createdDate' | 'beneficiary.memberId' | 'beneficiary.wixUserId'
    >
  ) => BalancesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => BalancesQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => BalancesQueryBuilder;
  find: () => Promise<BalancesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.benefit_programs.v1.balance.BalanceService.QueryBalances
 * @requiredField query
 */
export async function typedQueryBalances(
  query: BalanceQuery
): Promise<
  NonNullablePaths<
    QueryBalancesResponse,
    | `balances`
    | `balances.${number}._id`
    | `balances.${number}.beneficiary.anonymousVisitorId`
    | `balances.${number}.beneficiary.memberId`
    | `balances.${number}.beneficiary.wixUserId`
    | `balances.${number}.amount.available`
    | `balances.${number}.amount.reserved`
    | `balances.${number}.poolInfo._id`
    | `balances.${number}.poolInfo.status`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixBenefitProgramsV1Balance.queryBalances(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BalanceQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        'beneficiary.memberId',
        'beneficiary.wixUserId'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Balance,
  BalanceQuerySpec
>;
export type BalanceQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.
  See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
  for more information.  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  List of sort objects. 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order. Use `ASC` for ascending order or `DESC` for descending order.

  Default: `ASC`  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
    /** 
  When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
  a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.

  If multiple filters are provided, they are combined with AND operator.

  Example:
  Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
  and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
  { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] } 
  @internal: undefined,
  @maxSize: 10 
  */
    selectItemsBy?:
      | NonNullable<
          CommonQueryWithEntityContext['sort']
        >[number]['selectItemsBy']
      | null;
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    Balance,
    BalanceQuerySpec,
    BalanceQuery
  >(),
};

/**
 * Changes the number of available credits in a balance.
 *
 * You can adjust or set the number of available credits.
 * @param poolId - ID of the pool associated with the balance to change. This is also the ID of the balance.
 * @param idempotencyKey - Unique identifier, generated by the client.
 * Used to recognize repeated attempts to make the same request.
 * @public
 * @requiredField idempotencyKey
 * @requiredField options.operation
 * @requiredField poolId
 * @permissionId BENEFIT_PROGRAMS.BALANCE_CHANGE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.balance.BalanceService.ChangeBalance
 */
export async function changeBalance(
  poolId: string,
  idempotencyKey: string,
  options?: ChangeBalanceOptions
): Promise<
  NonNullablePaths<
    ChangeBalanceResponse,
    | `balance._id`
    | `balance.beneficiary.anonymousVisitorId`
    | `balance.beneficiary.memberId`
    | `balance.beneficiary.wixUserId`
    | `balance.amount.available`
    | `balance.amount.reserved`
    | `balance.poolInfo._id`
    | `balance.poolInfo.status`,
    4
  > & {
    __applicationErrorsType?: ChangeBalanceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    poolId: poolId,
    idempotencyKey: idempotencyKey,
    instructingParty: options?.instructingParty,
    type: options?.type,
    adjustOptions: options?.adjustOptions,
    setOptions: options?.setOptions,
    transactionDetails: options?.transactionDetails,
  });

  const reqOpts = ambassadorWixBenefitProgramsV1Balance.changeBalance(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          poolId: '$[0]',
          idempotencyKey: '$[1]',
          instructingParty: '$[2].instructingParty',
          type: '$[2].type',
          adjustOptions: '$[2].adjustOptions',
          setOptions: '$[2].setOptions',
          transactionDetails: '$[2].transactionDetails',
        },
        singleArgumentUnchanged: false,
      },
      ['poolId', 'idempotencyKey', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ChangeBalanceOptions
  extends ChangeBalanceOptionsOperationOneOf {
  /** Identity changing the balance. */
  instructingParty?: CommonIdentificationData;
  /** Balance change type. */
  type?: ChangeBalanceRequestTypeWithLiterals;
  /** Adjust the balance by a specific number of credits. */
  adjustOptions?: AdjustOptions;
  /** Set the balance to a specific number of credits. */
  setOptions?: SetOptions;
  /** Details to send to the transaction created from this balance change. */
  transactionDetails?: TransactionDetails;
}

/** @oneof */
export interface ChangeBalanceOptionsOperationOneOf {
  /** Adjust the balance by a specific number of credits. */
  adjustOptions?: AdjustOptions;
  /** Set the balance to a specific number of credits. */
  setOptions?: SetOptions;
  /**
   * Sets the initial balance to initialize the balance. If this isn't the first operation on the balance, the call will fail.
   * @internal
   */
  setInitialOptions?: SetInitialOptions;
}

/**
 * Reverts a transaction created by calling Change Balance.
 *
 * For example, if a transaction increased a balance's available credits by 5,
 * calling Revert Balance Change with the transaction's ID will decrease the balance's available credit by 5.
 *
 * > **Note:** If the Change Balance call set the balance, Revert Transaction reverts the amount the balance changed by, ensuring subsequent balance changes are still reflected after the reversion.
 * @param transactionId - ID of the transaction associated with the balance change to revert.
 * @param idempotencyKey - Ignored value, kept for backward compatibility. Idempotency is now handled by the system.
 * @public
 * @requiredField idempotencyKey
 * @requiredField transactionId
 * @permissionId BENEFIT_PROGRAMS.BALANCE_CHANGE
 * @applicableIdentity APP
 * @fqn wix.benefit_programs.v1.balance.BalanceService.RevertBalanceChange
 */
export async function revertBalanceChange(
  transactionId: string,
  idempotencyKey: string,
  options?: RevertBalanceChangeOptions
): Promise<
  RevertBalanceChangeResponse & {
    __applicationErrorsType?: RevertBalanceChangeApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    transactionId: transactionId,
    idempotencyKey: idempotencyKey,
    instructingParty: options?.instructingParty,
  });

  const reqOpts =
    ambassadorWixBenefitProgramsV1Balance.revertBalanceChange(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          transactionId: '$[0]',
          idempotencyKey: '$[1]',
          instructingParty: '$[2].instructingParty',
        },
        singleArgumentUnchanged: false,
      },
      ['transactionId', 'idempotencyKey', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RevertBalanceChangeOptions {
  /** Identity reverting the balance change. */
  instructingParty?: CommonIdentificationData;
}
